import {
  INavigationSubGroup,
  INavigationSubGroupContext,
  INavigationSubItem
} from "@/lib/interfaces/navigation-sub-group.interface";
import { ReportRouteNames } from "@/lib/utility/report.go-to-helper";
import { TicketRouteNames } from "@/lib/utility/ticket.go-to-helper";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { ResourceEnum } from "@/store/enum/authResourceEnum";
import { InboxEntityModule } from "@/store/modules/inbox.store";
import { UserModule } from "@/store/modules/me-user.store";
import { ProjectModule } from "@/store/modules/project.store";
import { ReportGroupModule } from "@/store/modules/report-group.store";

/**
 * Amount of children a sub group renders before the remaining ones are only reachable
 * via the overview item of the group.
 */
export const DEFAULT_MAX_NAVIGATION_SUB_ITEMS = 8;

/**
 * Icon of a single project in the navigation
 */
const PROJECT_SUB_ITEM_ICON = "mdi-format-list-bulleted-square";

/**
 * Icon of a single report group in the navigation
 */
const REPORT_GROUP_SUB_ITEM_ICON = "mdi-folder-outline";

/**
 * Entry route of a project.
 *
 * It renders the first custom view, like `view/0` does, and - unlike `view/0` - stays the
 * highlighted navigation item while the user switches between the views of the project.
 */
const PROJECT_HOME_ROUTE = "PROJECT_CUSTOM_VIEW_HOME";

/**
 * Entry route of a report group, see {@link PROJECT_HOME_ROUTE}
 */
const REPORT_GROUP_HOME_ROUTE = "REPORT_GROUP_CUSTOM_VIEW_HOME";

/**
 * Route of a single inbox. Its `inboxId` param is the identifier of the inbox, not its id.
 */
const INBOX_ROUTE = "PartnerMessageInbox";

/**
 * Route of the inbox overview, the navigation entry the inboxes are rendered below
 */
const INBOX_LIST_ROUTE = "PartnerMessageInboxList";

/**
 * Renders the projects of the partner below the `Projects` navigation entry.
 *
 * The projects are usually already in the store: `ProjectStore.reset` is dispatched for every
 * `partnerLoaded` event (see `Portal.vue`) and fetches the projects of the new partner.
 * `load` only kicks in if that did not happen, e.g. on a deep link into a view that does not
 * initialize the project lists.
 */
const projectSubGroup: INavigationSubGroup = {
  maxItems: DEFAULT_MAX_NAVIGATION_SUB_ITEMS,

  isLoading: () => ProjectModule.isLoading,

  load: async ({ partnerId }: INavigationSubGroupContext) => {
    // `ProjectStore.reset` is already fetching the projects of the partner, a second request
    // would share - and interleave with - the pager of the store
    if (!partnerId || ProjectModule.isLoading || ProjectModule.entities.length) {
      return;
    }

    if (!UserModule.abilities.can(ActionEnum.READ, ResourceEnum.PROJECT, partnerId)) {
      return;
    }

    await ProjectModule.fetchFirstPage({ partnerId });
  },

  items: ({ partnerId }: INavigationSubGroupContext) =>
    ProjectModule.entities
      .filter((project) => project.partnerId === partnerId && !project.closedAt)
      .sort((a, b) => a.title.localeCompare(b.title))
      .map<INavigationSubItem>((project) => ({
        key: project.id,
        title: project.title,
        icon: PROJECT_SUB_ITEM_ICON,
        to: { name: PROJECT_HOME_ROUTE, params: { partnerId, projectId: project.id } }
      }))
};

/**
 * Renders the report groups of the partner below the `Reports` navigation entry.
 *
 * Like the projects, the groups are usually already in the store because
 * `ReportGroupPaginationStore.reset` is dispatched on `partnerLoaded`.
 */
const reportGroupSubGroup: INavigationSubGroup = {
  maxItems: DEFAULT_MAX_NAVIGATION_SUB_ITEMS,

  isLoading: () => ReportGroupModule.isLoading,

  load: async ({ partnerId }: INavigationSubGroupContext) => {
    if (!partnerId || ReportGroupModule.isLoading || ReportGroupModule.entities.length) {
      return;
    }

    if (!UserModule.abilities.can(ActionEnum.READ, ResourceEnum.REPORT_GROUP, partnerId)) {
      return;
    }

    await ReportGroupModule.fetchFirstPage({ partnerId });
  },

  items: ({ partnerId }: INavigationSubGroupContext) =>
    ReportGroupModule.entities
      .filter((reportGroup) => reportGroup.partnerId === partnerId)
      .sort((a, b) => a.title.localeCompare(b.title))
      .map<INavigationSubItem>((reportGroup) => ({
        key: reportGroup.id,
        title: reportGroup.title,
        icon: REPORT_GROUP_SUB_ITEM_ICON,
        to: { name: REPORT_GROUP_HOME_ROUTE, params: { partnerId, reportGroupId: reportGroup.id } }
      }))
};

/**
 * Renders the inboxes of the partner below the `Inbox` navigation entry.
 *
 * The inboxes are usually already in the store: `InboxStore.reset` is dispatched on
 * `partnerLoaded` and fills `InboxEntityModule` before it registers a message store per inbox.
 */
const inboxSubGroup: INavigationSubGroup = {
  maxItems: DEFAULT_MAX_NAVIGATION_SUB_ITEMS,

  isLoading: () => InboxEntityModule.isLoading,

  load: async ({ partnerId }: INavigationSubGroupContext) => {
    if (!partnerId || InboxEntityModule.isLoading || InboxEntityModule.entities.length) {
      return;
    }

    // same ability `InboxStore.reset` checks before it loads the inboxes of the partner
    if (!UserModule.abilities.can(ActionEnum.READ, ResourceEnum.MESSAGE, partnerId)) {
      return;
    }

    await InboxEntityModule.fetchFirstPage({ partnerId });
  },

  items: ({ partnerId }: INavigationSubGroupContext) =>
    InboxEntityModule.entities
      .filter((inbox) => inbox.partnerId === partnerId)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map<INavigationSubItem>((inbox) => ({
        key: inbox.id,
        title: inbox.name || inbox.identifier,
        icon: inbox.icon,
        to: { name: INBOX_ROUTE, params: { partnerId, inboxId: inbox.identifier } }
      }))
};

/**
 * Side bar navigation entries that render their entities as a sub group, by route name.
 *
 * Add an entry here to turn a navigation entry into an expandable group.
 */
export const navigationSubGroups: Record<string, INavigationSubGroup> = {
  [TicketRouteNames.PROJECT_TABLE]: projectSubGroup,
  [ReportRouteNames.REPORT_LIST]: reportGroupSubGroup,
  [INBOX_LIST_ROUTE]: inboxSubGroup
};

/**
 * @param name the name of the route behind a navigation entry
 * @returns the sub group of the navigation entry, if it has one
 */
export function getNavigationSubGroup(name?: string | null): INavigationSubGroup | undefined {
  if (!name) {
    return undefined;
  }

  return navigationSubGroups[name];
}
